<template>
  <v-form ref="contractParams">
    <v-btn
        color="primary"
        :disabled="goldbackIncManagedLease"
        v-if="isNew"
        @click="setGoldbackIncManaged()"
        justify="right"
        class="mb-4 mr-4">
      Set Goldback, Inc Managed
      <v-icon v-if="goldbackIncManagedLease" class="mb-1 ml-2">mdi-check-bold</v-icon>
    </v-btn>
    <v-select
        :items="sourceOptions"
        item-text="name"
        :class="{'mobile-height': $vuetify.breakpoint.xsOnly}"
        v-model="formData.sourceOfContract"
        single-line
        return-object
        menu-props="bottom"
        solo
    />
    <v-text-field
        v-if="isNew && !goldbackIncManagedLease"
        solo
        label="Custom Contract Length (optional)"
        type="number"
        min="2"
        max="36"
        step="1"
        suffix="Months"
        v-model="formData.numMonths"
        :rules="rules.numMonths"
    />
    <v-select
        :items="commissionedParties"
        label="-- Commissioned Party (optional) --"
        item-text="name"
        :class="{'mobile-height': $vuetify.breakpoint.xsOnly}"
        v-model="formData.commissionedParty"
        single-line
        return-object
        menu-props="bottom"
        solo
    />
    <v-select
        :items="rateTiers"
        label="Rate Tier (optional)"
        item-text="name"
        :class="{'mobile-height': $vuetify.breakpoint.xsOnly}"
        v-model="formData.rateTier"
        :disabled="formData.useCustomRate"
        single-line
        return-object
        menu-props="bottom"
        solo
    />
    <v-checkbox
        v-model="formData.useCustomRate"
        label="Override with custom APR"
        class="mt-0 mb-3"
        @change="handleRateToggle"
    />
    <v-text-field
        solo
        label="Custom APR Percent"
        type="number"
        min="0"
        max="10"
        step=".1"
        suffix="%"
        v-model="formData.aprPercent"
        :disabled="!formData.useCustomRate"
        :rules="rules.aprPercent"
    />
    <v-text-field
        v-if="!goldbackIncManagedLease"
        solo
        label="Custom External ID (optional)"
        v-model="formData.externalIDInput"
        :rules="rules.externalID"
    />
    <v-text-field
        solo
        label="Custom Description (optional)"
        v-model="formData.descriptionInput"
        :rules="rules.description"
    />
    <v-select
        :items="['true', 'false']"
        label="Compound Payments? (optional)"
        item-text="name"
        :class="{'mobile-height': $vuetify.breakpoint.xsOnly}"
        v-model="formData.compoundPayments"
        single-line
        return-object
        menu-props="bottom"
        solo
    />
    <v-menu
        v-if="isNew"
        ref="leasePaymentDateMenu"
        v-model="formData.leasePaymentDateMenu"
        :close-on-content-click="false"
        :nudge-right="40"
        transition="scale-transition"
        offset-y
        max-width="290px"
        min-width="290px"
    >
      <template v-slot:activator="{ on }">
        <v-text-field
            solo
            label="Custom First Lease Payment Date (optional)"
            v-model="formData.firstPayment"
            readonly
            v-on="on"
        ></v-text-field>
      </template>
      <v-date-picker
          v-model="formData.firstPayment"
          :min="tomorrow()"
          no-title
          @input="leasePaymentDateMenu = false"
      ></v-date-picker>
    </v-menu>
    <v-menu
        v-if="!isNew"
        ref="endDateMenu"
        v-model="formData.endDateMenu"
        :close-on-content-click="false"
        :nudge-right="40"
        transition="scale-transition"
        offset-y
        max-width="290px"
        min-width="290px"
    >
      <template v-slot:activator="{ on }">
        <v-text-field
            solo
            label="Custom End Date (optional)"
            v-model="formData.customEndDate"
            readonly
            v-on="on"
        ></v-text-field>
      </template>
      <v-date-picker
          v-model="formData.customEndDate"
          :min="nextMonth()"
          :picker-date="nextMonth()"
          @input="endDateMenu = false"
      ></v-date-picker>
    </v-menu>
    <v-row>
      <v-spacer></v-spacer>
      <v-btn text color='red' @click="resetContractParams">RESET FORM</v-btn>
    </v-row>
  </v-form>
</template>

<script>
export default {
  name: 'LeasesDetailsForm',

  props: {
    isNew: {type: Boolean, default: false},
  },

  data() {
    return {
      leasePaymentDateMenu: false,
      goldbackIncManagedLease: false,
      endDateMenu: false,
      sourceOptions: [],
      commissionedParties: [],
      rateTiers: [],

      rules: {
        // --- fields for which full validation is required now because it was required in sign up ---  //
        aprPercent: [
          (v) => /^[.0-9\s]+$/i.test(v) || 'APR Percent must be numeric',
        ],

        externalID: [
          (v) => /^[0-9\s]+$/i.test(v) || 'External ID must be numeric',
          (v) => (v !== '4') || 'External ID 4 is reserved for Goldback, Inc'
        ],

        description: [
          (v) => /^[.a-zA-Z0-9\s]+$/i.test(v) || 'Description must be alphanumeric',
          (v) => (!!v && v.length <= 100) || 'Description cannot be longer than 100 characters'
        ],

        numMonths: [
          (v) => /^[.0-9\s]+$/i.test(v) || 'Contract length must be numeric',
          (v) => (v !== '1') || 'Setting a lease for \'1 month\' makes it indefinite'
        ],
      },

      formData: {
        commissionedParty: {email: null},
        firstPayment: null,
        aprPercent: null,
        compoundPayments: null,
        descriptionInput: null,
        externalIDInput: null,
        customEndDate: null,
        numMonths: null,
        sourceOfContract: {name: 'Alpine Gold'},
        rateTier: null,
        useCustomRate: false,
      }
    }
  },

  methods: {
    nextMonth() {
      let date = new Date();
      date.setMonth(date.getMonth() + 1);
      return date.toISOString().split('T')[0];
    },
    tomorrow() {
      let date = new Date();
      date.setDate(date.getDate() + 1);
      return date.toJSON();
    },
    setGoldbackIncManaged: function () {
      this.goldbackIncManagedLease = true
      this.numMonths = 1;
      this.externalIDInput = 4;
    },

    handleRateToggle() {
      if (this.formData.useCustomRate) {
        this.rateTier = null;
      } else {
        this.aprPercent = null;
      }
    },

    resetContractParams: function () {
      if (this.$refs.contractParams) this.$refs.contractParams.reset();
      this.goldbackIncManagedLease = false;
      this.formData.sourceOfContract = {name: 'Alpine Gold'};
      this.formData.firstPayment = null;
      this.formData.aprPercent = null;
      this.formData.useCustomRate = false;
      this.formData.rateTier = null;
      this.formData.numMonths = null;
      this.formData.descriptionInput = null;
      this.formData.externalIDInput = null;
      this.formData.customEndDate = null;
      this.formData.compoundPayments = null;
      this.formData.commissionedParty = {email: null};
    },

    getFormData() {
      let data = {};
      if (this.isNew) {
        data = {
          numMonths: this.formData.numMonths ?? 12,
          description: this.formData.descriptionInput ?? 'Lease',
          externalID: this.formData.externalIDInput ?? 0,
          firstPayment: this.formData.firstPayment,
          sourceOfContract: this.formData.sourceOfContract.name,
          commissionedEmail: this.formData.commissionedParty.email,
          compound: this.formData.compoundPayments === 'true',
        };
      } else {
        data = {
          description: this.formData.descriptionInput, //allow null
          externalID: this.formData.externalIDInput, //allow null
          sourceOfContract: this.formData.sourceOfContract.name, //allow null
          commissionedEmail: this.formData.commissionedParty.email, //allow null
          endDate: this.formData.customEndDate,
          compound: this.formData.compoundPayments === 'true',
        };
      }

      // Add either rate tier or custom APR based on toggle
      if (this.formData.useCustomRate && this.formData.aprPercent) {
        data.aprPercent = this.formData.aprPercent / 100;
      } else if (!this.formData.useCustomRate && this.formData.rateTier) {
        data.rateTier = this.formData.rateTier.value;
      }

      return data;
    },

    getSources: async function () {
      let res = await this.$axios.get(`/admin/list/sources`);
      this.sourceOptions = res.data;
    },
    getRateTiers: async function () {
      let res = await this.$axios.get(`/admin/lease/tiers`);

      this.rateTiers = res.data.map((tier) => ({
        name: tier.name,
        value: tier.id
      }));
    },

    getSales: async function () {
      let res = await this.$axios.get(`/admin/list/usernamesByScope`, {
        params: {
          filter: 'SALES'
        }
      });
      this.commissionedParties = res.data;
    },
  },

  mounted: async function () {
    await this.getSources();
    await this.getRateTiers();
    await this.getSales();
  },

  watch: {},
}
</script>

<style scoped>

</style>